import { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5000/api";
const CAPGEMINI_REGEX = /^[a-zA-Z0-9._%+\-]+@capgemini\.com$/;

interface Member {
  _id: string;
  name: string;
  email: string;
  role: { name: string } | string;
}

const roleName = (m: Member) =>
  typeof m.role === "object" ? m.role.name : m.role;

export function AddMember() {
  const { token } = useAuth();
  const [form, setForm] = useState({ role: "mentor", name: "", email: "", password: "" });
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch existing mentors + jury on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/admin/members`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setMembers(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Name must be at least 2 characters."); return;
    }
    if (!CAPGEMINI_REGEX.test(form.email.trim().toLowerCase())) {
      setError("Only Capgemini email addresses are accepted (e.g. example@capgemini.com)."); return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters."); return;
    }
    if (!/[A-Z]/.test(form.password)) {
      setError("Password must contain at least one uppercase letter."); return;
    }
    if (!/[0-9]/.test(form.password)) {
      setError("Password must contain at least one number."); return;
    }

    setSubmitting(true);
    setSuccess("");
    try {
      const res = await fetch(`${BASE}/admin/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), password: form.password.trim(), roleName: form.role }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message ?? "Failed to create member"); return; }
      setMembers((prev) => [...prev, { _id: data.user.id, name: data.user.name, email: data.user.email, role: data.user.role }]);
      setForm({ role: form.role, name: "", email: "", password: "" });
      setSuccess(`${form.role === "mentor" ? "Mentor" : "Jury member"} added successfully.`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.message ?? "Failed to create member");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      const res = await fetch(`${BASE}/admin/members/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setError(d.message ?? "Failed to delete"); return; }
      setMembers((prev) => prev.filter((m) => m._id !== id));
    } catch (err: any) {
      setError(err.message ?? "Failed to delete");
    }
  };

  const mentorCount = members.filter((m) => roleName(m) === "mentor").length;
  const juryCount = members.filter((m) => roleName(m) === "jury").length;
  const roleLabel = form.role === "mentor" ? "Mentor" : "Jury Member";

  return (
    <div className="flex flex-col gap-[32px]">
      {/* Header */}
      <div className="h-[160px] rounded-[16px] px-[12px] py-[32px]">
        <p className="font-['Inter:Medium',sans-serif] font-medium leading-[16.5px] text-[#6e7891] text-[11px] tracking-[0.275px] uppercase">
          MEMBER MANAGEMENT
        </p>
        <h1 className="font-['Inter:Regular',sans-serif] font-normal leading-[42px] text-[28px] text-[#1a2332] mt-[8px]">
          Add <span className="font-['Inter:Medium',sans-serif] font-medium">{roleLabel}</span>
        </h1>
        <p className="font-['Inter:Regular',sans-serif] font-normal leading-[21px] text-[#6e7891] text-[14px]">
          Create and manage mentors and jury members for your hackathons.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-[24px]">
        {[
          { label: "Total Mentors", count: mentorCount, color: "#0058AB" },
          { label: "Total Jury Members", count: juryCount, color: "#FF9F43" },
        ].map(({ label, count, color }) => (
          <div
            key={label}
            className="h-[140px] rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[24px]"
            style={{ backgroundImage: "linear-gradient(136.592deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)" }}
          >
            <p className="font-['Inter:Medium',sans-serif] font-medium leading-[16.5px] text-[#8b93a8] text-[11px] tracking-[0.275px] uppercase mb-[8px]">
              {label}
            </p>
            <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[32px] text-[32px]" style={{ color }}>
              {loading ? "—" : count}
            </p>
          </div>
        ))}
      </div>

      {/* Form */}
      <div
        className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[32px]"
        style={{ backgroundImage: "linear-gradient(157.593deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)" }}
      >
        <h2 className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[27px] text-[#1a2332] text-[18px] mb-[24px]">
          Add New {roleLabel}
        </h2>

        {error && (
          <div className="mb-[16px] p-[12px] bg-red-50 border border-red-300 text-red-700 rounded-[8px] text-[14px]">{error}</div>
        )}
        {success && (
          <div className="mb-[16px] p-[12px] bg-green-50 border border-green-300 text-green-700 rounded-[8px] text-[14px]">{success}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-[20px]">
          <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-[8px]">
              <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[14px]">
                Role<span className="text-red-500">*</span>
              </label>
              <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
                <SelectTrigger className="border border-[#e0e4f0] rounded-[8px] px-[12px] py-[10px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mentor">Mentor</SelectItem>
                  <SelectItem value="jury">Jury Member</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-[8px]">
              <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[14px]">
                Name<span className="text-red-500">*</span>
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Full name"
                className="border border-[#e0e4f0] rounded-[8px]"
                required
              />
            </div>

            <div className="flex flex-col gap-[8px]">
              <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[14px]">
                Email<span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="example@capgemini.com"
                className="border border-[#e0e4f0] rounded-[8px]"
                required
              />
            </div>

            <div className="flex flex-col gap-[8px]">
              <label className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[14px]">
                Password<span className="text-red-500">*</span>
              </label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                placeholder="Temporary password"
                className="border border-[#e0e4f0] rounded-[8px]"
                required
              />
            </div>
          </div>

          <div className="flex gap-[12px] justify-end pt-[12px]">
            <button
              type="button"
              onClick={() => { setForm({ role: "mentor", name: "", email: "", password: "" }); setError(""); }}
              className="px-[20px] py-[10px] bg-white border border-[#6e7891] text-[#6e7891] rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] hover:bg-[#f5f7fb] transition-colors"
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-[24px] py-[10px] bg-[#0058ab] text-white rounded-[12px] font-['Inter:Medium',sans-serif] font-medium text-[13px] shadow-[0px_4px_12px_0px_rgba(0,88,171,0.25)] hover:bg-[#004a94] transition-colors disabled:opacity-60"
            >
              {submitting ? "Adding..." : `Add ${roleLabel}`}
            </button>
          </div>
        </form>
      </div>

      {/* Members List */}
      <div>
        <h2 className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[27px] text-[#1a2332] text-[18px] mb-[20px]">
          {loading ? "Loading members..." : `All Members (${members.length})`}
        </h2>

        {!loading && members.length === 0 && (
          <p className="text-[#6e7891] text-[14px]">No mentors or jury members added yet.</p>
        )}

        <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2 lg:grid-cols-3">
          {members.map((m) => {
            const role = roleName(m);
            return (
              <div
                key={m._id}
                className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[20px]"
                style={{ backgroundImage: "linear-gradient(157.593deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)" }}
              >
                <div className="flex items-start justify-between mb-[12px]">
                  <span
                    className="px-[12px] py-[4px] rounded-[8px] font-['Inter:Medium',sans-serif] font-medium text-[11px] text-white"
                    style={{ backgroundColor: role === "mentor" ? "#0058AB" : "#FF9F43" }}
                  >
                    {role === "mentor" ? "Mentor" : "Jury Member"}
                  </span>
                  <button
                    onClick={() => handleRemove(m._id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove member"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[21px] text-[#1a2332] text-[14px] mb-[4px]">
                  {m.name}
                </p>
                <p className="font-['Inter:Regular',sans-serif] font-normal leading-[19.5px] text-[#6e7891] text-[13px]">
                  {m.email}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
